import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { atom, AtomEffect, RecoilState, useRecoilCallback } from "recoil";
import { recoilPersist } from "recoil-persist";
import { PersistTypes } from "./types";
import { GridPaginationModel } from "@mui/x-data-grid";
import { useEffect } from "react";

type Persisister = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key in PersistTypes]: AtomEffect<any>[];
};

export type TableStateExtended = GridInitialStatePro & {
  columns?: GridInitialStatePro["columns"] & {
    dimensions?: Record<string, { width: number }>;
  };
};

interface TablesStates {
  [key: string]: RecoilState<TableStateExtended | undefined>;
}

interface TablesPaginationStates {
  [key: string]: RecoilState<GridPaginationModel>;
}

const tableStates: TablesStates = {};
const tablePaginationStates: TablesPaginationStates = {};

const { persistAtom: sessionStoragePersist } = recoilPersist({ storage: sessionStorage });
const { persistAtom } = recoilPersist();

const persister: Persisister = {
  runTime: [],
  session: [sessionStoragePersist],
  client: [persistAtom],
};

export function makeGridState(id: string, persistType?: PersistTypes) {
  if (!persistType) return tableStates.DummyTableState;

  if (tableStates[id]) {
    return tableStates[id];
  }

  const state = atom<TableStateExtended | undefined>({
    key: `TableState${id}`,
    effects_UNSTABLE: [...persister[persistType]],
    default: undefined,
  });
  tableStates[id] = state;
  return state;
}

// grid pagination has own state, for more reliable results
export function makeGridPaginationState({
  tableId,
  persistType,
  initState,
}: {
  tableId: string;
  persistType?: PersistTypes;
  initState?: { page?: number; pageSize?: number } | undefined;
}) {
  if (!persistType) return tablePaginationStates.DummyTablePaginationState;

  if (tablePaginationStates[tableId]) {
    return tablePaginationStates[tableId];
  }
  const state = atom<GridPaginationModel>({
    key: `TablePaginationState${tableId}`,
    default: {
      page: initState?.page ?? 0,
      pageSize: initState?.pageSize ?? 25,
    },
  });
  tablePaginationStates[tableId] = state;
  return state;
}

// Api refs of the grids that are currently mounted, keyed by table id. The persisted state is only
// read as `initialState`, so a mounted grid has to be told about a filter reset through its api.
type ResettableGridApi = { setFilterModel?: (model: { items: []; quickFilterValues: [] }) => void };
const mountedGridApiRefs = new Map<string, Set<React.RefObject<ResettableGridApi | null>>>();

export function useRegisterGridApiRef(tableId: string, apiRef?: React.RefObject<ResettableGridApi | null>) {
  useEffect(() => {
    if (!apiRef) return;
    const refs = mountedGridApiRefs.get(tableId) ?? new Set<React.RefObject<ResettableGridApi | null>>();
    refs.add(apiRef);
    mountedGridApiRefs.set(tableId, refs);
    return () => {
      refs.delete(apiRef);
      if (refs.size === 0) mountedGridApiRefs.delete(tableId);
    };
  }, [tableId, apiRef]);
}

// Clears the filter of every grid, both the persisted state and the live grid api, so navigating via
// the main navigation starts each table from an unfiltered view. `excludeTableIds` keeps the filter of
// the table that triggered the reset, e.g. a list whose search should survive selecting one of its rows.
export function useResetGridFilters() {
  return useRecoilCallback(
    ({ set }) =>
      (excludeTableIds?: string[]) => {
        Object.entries(tableStates).forEach(([tableId, gridState]) => {
          if (excludeTableIds?.includes(tableId)) return;
          set(gridState, (prevState) =>
            prevState ? { ...prevState, filter: { filterModel: { items: [], quickFilterValues: [] } } } : prevState
          );
        });
        mountedGridApiRefs.forEach((apiRefs, tableId) => {
          if (excludeTableIds?.includes(tableId)) return;
          apiRefs.forEach((apiRef) => apiRef.current?.setFilterModel?.({ items: [], quickFilterValues: [] }));
        });
      },
    []
  );
}

export const dummyTableState = atom<TableStateExtended | undefined>({
  key: `DummyTableState`,
  default: undefined,
});

dummyTableState.__cTag = () => {};
tableStates.DummyTableState = dummyTableState;
